import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { motion, type Variants } from 'framer-motion';
import {
  Children,
  cloneElement,
  isValidElement,
  type ReactNode,
  useMemo,
  useRef,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type Modifier, usePopper } from 'react-popper';

import useOverlayTransition from '../hooks/useOverlayTransition.js';
import {
  sliceFlattenedSuggestions,
  useShowMore,
} from '../hooks/useShowMore.js';
import { filterFieldMessages } from '../messages.js';
import { AriaLiveInfo } from './AriaLiveInfo.js';
import { FallbackKeySuggestionGroup } from './FallbackKeySuggestions.js';
import { LogicalOperatorSuggestionsGroup } from './LogicalOperatorSuggestions.js';
import { PastedContentSuggestionGroup } from './PastedContentSuggestions.js';
import {
  PinnedSuggestionGroup,
  RecentSuggestionGroup,
} from './RecentPinnedSuggestionItem.js';
import { SearchSuggestionGroup } from './SearchSuggestions.js';
import * as suggestionStyles from './Suggestion.sty.js';
import { Suggestion } from './Suggestion.js';
import type { FlattenedSuggestionItem } from './Suggestion.types.js';
import * as suggestionGroupStyles from './SuggestionGroup.sty.js';
import { SuggestionGroupLabel } from './SuggestionGroupLabel.js';
import * as styles from './SuggestionsOverlay.sty.js';
import { Button, ButtonProps } from '../../../buttons/button/Button.js';
import { ProgressCircle } from '../../../content/progress/ProgressCircle.js';
import { VisuallyHidden } from '../../../core/components/visually-hidden/VisuallyHidden.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../core/types/data-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import type { WithChildren } from '../../../core/types/with-children.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { Surface } from '../../../layouts/surface/Surface.js';
import { CSSHighlight } from '../components/CSSHighlight.js';
import { useFilterFieldContext } from '../hooks/useFilterFieldContext.js';
import { flattenSuggestions } from '../utils/suggestions-utils/flatten-suggestions.js';

/**
 * Converts a groupId to a valid HTML id attribute value safe for use in aria-describedby.
 * HTML ids must not contain spaces (space is the delimiter in aria-describedby token lists).
 */
function toGroupLabelId(groupId: string): string {
  return `group-label-${groupId.replace(/\s+/g, '-')}`;
}

function renderFlattenedSuggestionItem(
  flatItem: FlattenedSuggestionItem,
  trailing: string | undefined,
): { child: ReactNode; key: string } {
  switch (flatItem.kind) {
    case 'group-header': {
      let child: ReactNode;

      if (isValidElement(flatItem.label)) {
        child = flatItem.label;
      } else if (flatItem.label) {
        child = <SuggestionGroupLabel>{flatItem.label}</SuggestionGroupLabel>;
      } else {
        child = null;
      }

      return {
        child: (
          <div
            className={suggestionGroupStyles.groupHeaderSeparator}
            role="presentation"
          >
            {child}
          </div>
        ),
        key: `group-header-${flatItem.groupId}`,
      };
    }
    case 'suggestion': {
      const ariaDescribedBy = flatItem.groupId
        ? toGroupLabelId(flatItem.groupId)
        : undefined;

      return {
        child: (
          <Suggestion
            {...flatItem.data}
            trailing={flatItem.data.trailing ?? trailing}
            aria-describedby={ariaDescribedBy}
          />
        ),
        key: `${flatItem.data.value ?? flatItem.data.displayValue ?? ''}`,
      };
    }
    case 'react-node': {
      const groupLabelId = flatItem.groupId
        ? toGroupLabelId(flatItem.groupId)
        : undefined;
      let child = flatItem.node;
      let key = '';

      if (isValidElement(flatItem.node) && groupLabelId) {
        // Merge with any existing aria-describedby from the consumer element.
        const existing =
          (flatItem.node.props as Record<string, unknown>)[
            'aria-describedby'
          ] ?? '';
        const merged = existing ? `${existing} ${groupLabelId}` : groupLabelId;

        child = cloneElement(flatItem.node, {
          'aria-describedby': merged,
        });
        key = `react-node-${flatItem.node.key}`;
      }

      return { child, key };
    }
  }
}

function SuggestionContent(
  props: Readonly<{
    optionVirtualizer: Virtualizer<HTMLDivElement, Element>;
    flattenedSuggestions: FlattenedSuggestionItem[];
    isSliced: boolean;
    controlProps: ButtonProps<'button'>;
  }>,
) {
  const { optionVirtualizer, flattenedSuggestions, isSliced, controlProps } =
    props;

  const intl = useIntl();
  const {
    term,
    editorView,
    autoSuggestions,
    recentSuggestionValues,
    pinnedSuggestionValues,
    logicalOperatorSuggestionValues,
    pastedContentSuggestionValues,
    searchSuggestionValues,
    fallbackKeySuggestionValues,
    statementSuggestionValues,
    filterFieldId,
    parserConfig,
    isValidSearch,
  } = useFilterFieldContext();

  const { trailing } = autoSuggestions;

  const showLogicalOperators = logicalOperatorSuggestionValues.length > 0;
  const showRecents = recentSuggestionValues.length > 0;
  const showPinned = pinnedSuggestionValues.length > 0;
  const showFallbackKeySuggestions = fallbackKeySuggestionValues.length > 0;
  const showStatementSuggestions = statementSuggestionValues.length > 0;
  const showSearchSuggestions =
    isValidSearch?.current &&
    searchSuggestionValues.length > 0 &&
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    (parserConfig?.searchConversion ?? false);
  const showPastedContentSuggestions = pastedContentSuggestionValues.length > 0;

  const showBuiltInSuggestions =
    showRecents ||
    showPinned ||
    showLogicalOperators ||
    showPastedContentSuggestions ||
    showSearchSuggestions ||
    showFallbackKeySuggestions ||
    showStatementSuggestions;

  const isEmpty = editorView?.state.doc.toString() === '';
  const childrenCount = flattenedSuggestions.length;

  if (childrenCount === 0 && !showBuiltInSuggestions) {
    return (
      <Suggestion
        trailing=""
        displayValue={intl.formatMessage(
          filterFieldMessages.suggestions.noSuggestions,
        )}
      />
    );
  }
  return (
    <div id={`suggestions-${filterFieldId}`}>
      {childrenCount > 0 ? (
        <CSSHighlight
          term={term ?? ''}
          // Two highlight targets: the primary value text and any details
          // element flagged matchable (see `matchableSuggestionDetails` in
          // Suggestion.css.ts). `:not(:has(mark))` keeps the highlight off
          // elements that already contain a hand-rendered <mark>.
          selector={`.${suggestionStyles.value}:not(:has(mark)), .${suggestionStyles.matchableSuggestionDetails}:not(:has(mark))`}
          highlightName="filterfield-suggestions-highlight"
        >
          <div
            style={{
              width: '100%',
              position: 'relative',
              height: optionVirtualizer.getTotalSize(),
              display: 'grid',
              gridTemplateRows: optionVirtualizer.measurementsCache
                .map((el) => `${el.size}px`)
                .join(' '),
            }}
            key={`wrapper-${childrenCount}`}
          >
            {optionVirtualizer.getVirtualItems().map((virtualRow) => {
              const flatItem = flattenedSuggestions[virtualRow.index];
              const { child, key: suggestionKey } =
                renderFlattenedSuggestionItem(flatItem, trailing);

              return (
                <div
                  key={`${virtualRow.key}-${suggestionKey}`}
                  className={clsx(
                    'strato-filter-field-suggestion',
                    suggestionStyles.virtualizedSuggestion,
                  )}
                  // data-index is required for the virtualizer to map the measure
                  // element correctly to the measured index.
                  data-index={virtualRow.index}
                  style={{
                    gridRow: virtualRow.index + 1,
                    minWidth: 0,
                    height: 'fit-content',
                  }}
                  ref={(el) => {
                    optionVirtualizer.measureElement(el);
                  }}
                >
                  {child}
                </div>
              );
            })}
          </div>
        </CSSHighlight>
      ) : null}
      {isSliced && (
        <div role="presentation">
          <Button {...controlProps} />
        </div>
      )}
      {showBuiltInSuggestions && (
        <>
          <PastedContentSuggestionGroup />
          <FallbackKeySuggestionGroup />
          <LogicalOperatorSuggestionsGroup />
          {showSearchSuggestions && !isEmpty && <SearchSuggestionGroup />}
          <CSSHighlight
            className={suggestionGroupStyles.builtInSuggestionsContainer}
            term={editorView?.state.doc.toString() ?? ''}
            selector={`.${suggestionStyles.value}`}
            highlightName="filterfield-suggestions-highlight"
          >
            <RecentSuggestionGroup />
            <PinnedSuggestionGroup />
          </CSSHighlight>
          {showSearchSuggestions && isEmpty && <SearchSuggestionGroup />}
        </>
      )}
    </div>
  );
}

// Popper modifier to sync the max-height of the suggestions overlay
const heightModifier: Modifier<'filterfield-height-handler'> = {
  name: 'filterfield-height-handler',
  enabled: true,
  fn: ({ state }): void => {
    const viewportHeight = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0,
    );

    const maxHeightBase = Math.max(viewportHeight / 2, 320);
    state.styles.popper.maxHeight = `${
      maxHeightBase - state.rects.reference.height - 20
    }px`;
  },
  phase: 'beforeWrite',
  requires: ['computeStyles'],
};

/**
 * Accepted properties for FilterFieldSuggestions.
 * @public
 */
export type FilterFieldSuggestionsProps = WithChildren &
  StylingProps &
  DataTestId &
  BehaviorTrackingProps & {
    /** Whether the suggestions are loading. If true, a loading indicator is shown and otherwise the suggestions are shown. */
    loading?: boolean;
  };

// Animation variants for the Suggestions component
const suggestionsAnimationVariants: Variants = {
  inital: {
    translateY: '-10px',
    // Compensate the parent scaling the content to 0.8. Due to round errors when the height gets taller
    // the content sometimes got larger than the intended which lead to a "vertigo" effect. By adding
    // 0.05 we can counteract that effect somewhat.
    scaleY: 1 / 0.85,
    opacity: 0,
  },
  default: {
    translateY: 0,
    scaleY: 1,
    opacity: 1,
  },
  exit: {
    translateY: '-5px',
    opacity: 0,
  },
};

function getSuggestionsCount(
  defaultSuggestionsCount: number | { empty: number; filled: number },
  isEmpty: boolean,
): number {
  if (typeof defaultSuggestionsCount === 'number') {
    return defaultSuggestionsCount;
  }
  return defaultSuggestionsCount[isEmpty ? 'empty' : 'filled'];
}

/**
 * Compound component of the FilterField to apply suggestions.
 * @public
 */
export const Suggestions = (props: FilterFieldSuggestionsProps) => {
  const {
    loading,
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: FilterFieldSuggestionsProps = props;
  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);

  const { autoSuggestions, defaultSuggestionsCount, triggerRef, editorView } =
    useFilterFieldContext();

  const intl = useIntl();

  const transition = useOverlayTransition(true);

  const parentRef = useRef<HTMLDivElement | null>(null);

  const childrenArray = useMemo(() => Children.toArray(children), [children]);

  const { flattenedSuggestionItems, groupMetadata } = useMemo(
    () =>
      flattenSuggestions([
        ...Object.values(autoSuggestions.groupedSuggestions ?? {}).filter(
          (group) => group && (group.suggestions?.length ?? 0) > 0,
        ),
        ...childrenArray,
      ]),
    [autoSuggestions.groupedSuggestions, childrenArray],
  );

  const isEmpty = editorView?.state.doc.toString() === '';

  const suggestionsCount = getSuggestionsCount(
    defaultSuggestionsCount,
    isEmpty,
  );

  const {
    items: slicedSuggestions,
    isSliced,
    controlProps,
  } = useShowMore({
    items: flattenedSuggestionItems,
    primaryCount: suggestionsCount,
    sliceFn: sliceFlattenedSuggestions,
  });

  const optionVirtualizer = useVirtualizer({
    count: slicedSuggestions.length,
    getScrollElement: () => parentRef.current,
    overscan: 5,
    estimateSize: () => 32,
  });

  const suggestionsRef = useRef<HTMLDivElement | null>(null);

  const { styles: positioningStyles } = usePopper(
    triggerRef,
    suggestionsRef.current,
    {
      placement: 'bottom-start',
      modifiers: [heightModifier],
    },
  );

  return (
    <Surface
      className={clsx(consumerClassName, styles.suggestionsOverlay)}
      style={consumerStyle}
      elevation="floating"
      ref={suggestionsRef}
      data-testid={dataTestId}
      {...behavioralTrackingProps}
    >
      <AriaLiveInfo flattenedSuggestionItems={flattenedSuggestionItems} />
      {groupMetadata.length > 0 && (
        <VisuallyHidden>
          {groupMetadata.map(({ groupId, label }) => (
            <span key={groupId} id={toGroupLabelId(groupId)}>
              {label}
            </span>
          ))}
        </VisuallyHidden>
      )}
      <div
        className={styles.suggestionsScrollContainerWrapper}
        role="listbox"
        aria-expanded="true"
        aria-label={intl.formatMessage(
          filterFieldMessages.suggestions.a11y.ariaLabel,
        )}
      >
        <motion.div
          className={clsx(
            styles.suggestionsScrollContainer,
            'strato-filter-field-suggestions',
          )}
          ref={parentRef}
          style={{ maxHeight: positioningStyles.popper.maxHeight }}
          variants={suggestionsAnimationVariants}
          initial="inital"
          animate="default"
          exit="exit"
          transition={transition}
        >
          {loading ? (
            <Flex
              role="presentation"
              justifyContent="start"
              px={8}
              py={6}
              className={suggestionStyles.suggestionsLoading}
            >
              <ProgressCircle size="small" aria-label="Loading suggestions" />
              <FormattedMessage {...filterFieldMessages.suggestions.loading} />
            </Flex>
          ) : (
            <SuggestionContent
              optionVirtualizer={optionVirtualizer}
              flattenedSuggestions={slicedSuggestions}
              isSliced={isSliced}
              controlProps={controlProps}
            />
          )}
        </motion.div>
      </div>
    </Surface>
  );
};

Suggestions.displayValue = 'FilterField.Suggestions';
